import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Activity, 
  Shield, 
  TrendingUp, 
  Calendar, 
  Settings, 
  Bell, 
  User, 
  Battery,
  Wifi,
  Award,
  Target,
  AlertTriangle,
  ChevronRight,
  Play,
  Pause
} from 'lucide-react';

const Dashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isRecording, setIsRecording] = useState(false);

  const stats = [
    {
      icon: <Activity className="w-8 h-8" />,
      title: 'Total Sessions',
      value: '47',
      change: '+12%',
      trend: 'up'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Impacts Detected',
      value: '1,234',
      change: '-8%',
      trend: 'down'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Performance Score',
      value: '94',
      change: '+3%',
      trend: 'up'
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Injury Risk',
      value: 'Low',
      change: 'Stable',
      trend: 'stable'
    }
  ];

  const recentSessions = [
    {
      id: 1,
      date: '2024-01-15',
      type: 'Training',
      duration: '90 min',
      impacts: 23,
      score: 92
    },
    {
      id: 2,
      date: '2024-01-13',
      type: 'Match',
      duration: '95 min',
      impacts: 45,
      score: 88
    },
    {
      id: 3,
      date: '2024-01-11',
      type: 'Training',
      duration: '75 min',
      impacts: 18,
      score: 95
    },
    {
      id: 4,
      date: '2024-01-09',
      type: 'Training',
      duration: '80 min',
      impacts: 21,
      score: 91
    }
  ];

  const deviceStatus = {
    battery: 85,
    connection: 'Connected',
    lastSync: '2 minutes ago',
    firmware: 'v2.1.3'
  };

  const insights = [
    {
      type: 'warning',
      title: 'High Impact Detection',
      message: 'Your right shin received 15% more impacts than usual during yesterday\'s training.',
      time: '2 hours ago'
    },
    {
      type: 'success',
      title: 'Performance Improvement',
      message: 'Your agility score improved by 8% compared to last week.',
      time: '1 day ago'
    },
    {
      type: 'info',
      title: 'Training Reminder',
      message: 'You have a scheduled training session tomorrow at 3:00 PM.',
      time: '2 days ago'
    }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <Activity className="w-5 h-5" /> },
    { id: 'performance', label: 'Performance', icon: <TrendingUp className="w-5 h-5" /> },
    { id: 'safety', label: 'Safety', icon: <Shield className="w-5 h-5" /> },
    { id: 'settings', label: 'Settings', icon: <Settings className="w-5 h-5" /> }
  ];

  const toggleRecording = () => {
    setIsRecording(!isRecording);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-cresento-green-600 rounded-full flex items-center justify-center text-white font-bold">
                JD
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-800">Welcome back, John</h1>
                <p className="text-sm text-gray-600">Ready for today's training?</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleRecording}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  isRecording
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-cresento-green-600 hover:bg-cresento-green-700 text-white'
                }`}
              >
                {isRecording ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                {isRecording ? 'Stop Session' : 'Start Session'}
              </motion.button>
              <button className="p-2 hover:bg-gray-100 rounded-lg">
                <Bell className="w-5 h-5 text-gray-600" />
              </button>
              <button className="p-2 hover:bg-gray-100 rounded-lg">
                <User className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Tab Navigation */}
        <div className="mb-8">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-cresento-green-600 text-white'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-lg"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-cresento-green-600">{stat.icon}</div>
                    <span className={`text-sm font-medium ${
                      stat.trend === 'up' ? 'text-green-600' : 
                      stat.trend === 'down' ? 'text-red-600' : 'text-gray-600'
                    }`}>
                      {stat.change}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-1">{stat.value}</h3>
                  <p className="text-gray-600">{stat.title}</p>
                </motion.div>
              ))}
            </div>

            {/* Device Status */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-white rounded-xl p-6 shadow-lg"
            >
              <h2 className="text-xl font-semibold mb-4 text-gray-800">Device Status</h2>
              <div className="grid md:grid-cols-4 gap-6">
                <div className="flex items-center gap-3">
                  <Battery className="w-6 h-6 text-cresento-green-600" />
                  <div>
                    <p className="text-sm text-gray-600">Battery</p>
                    <p className="font-semibold text-gray-800">{deviceStatus.battery}%</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Wifi className="w-6 h-6 text-cresento-green-600" />
                  <div>
                    <p className="text-sm text-gray-600">Connection</p>
                    <p className="font-semibold text-gray-800">{deviceStatus.connection}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Activity className="w-6 h-6 text-cresento-green-600" />
                  <div>
                    <p className="text-sm text-gray-600">Last Sync</p>
                    <p className="font-semibold text-gray-800">{deviceStatus.lastSync}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Settings className="w-6 h-6 text-cresento-green-600" />
                  <div>
                    <p className="text-sm text-gray-600">Firmware</p>
                    <p className="font-semibold text-gray-800">{deviceStatus.firmware}</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Recent Sessions and Insights */}
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Recent Sessions */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="bg-white rounded-xl shadow-lg"
              >
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-800">Recent Sessions</h2>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {recentSessions.map((session) => (
                      <div key={session.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-800">{session.type}</p>
                          <p className="text-sm text-gray-600">{session.date}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">{session.duration}</p>
                          <p className="text-sm text-gray-600">{session.impacts} impacts</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-cresento-green-600">{session.score}</p>
                          <p className="text-sm text-gray-600">Score</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Insights */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="bg-white rounded-xl shadow-lg"
              >
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-800">AI Insights</h2>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {insights.map((insight, index) => (
                      <div key={index} className="flex gap-3 p-4 bg-gray-50 rounded-lg">
                        <div className={`w-2 h-2 rounded-full mt-2 ${
                          insight.type === 'warning' ? 'bg-yellow-500' :
                          insight.type === 'success' ? 'bg-green-500' : 'bg-blue-500'
                        }`} />
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-800 mb-1">{insight.title}</h3>
                          <p className="text-sm text-gray-600 mb-2">{insight.message}</p>
                          <p className="text-xs text-gray-500">{insight.time}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        )}

        {/* Performance Tab */}
        {activeTab === 'performance' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-xl p-8 shadow-lg"
          >
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Performance Analytics</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="h-64 bg-gradient-to-br from-cresento-green-100 to-cresento-green-200 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <TrendingUp className="w-16 h-16 text-cresento-green-600 mx-auto mb-4" />
                  <p className="text-gray-600">Performance chart will be displayed here</p>
                </div>
              </div>
              <div className="h-64 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <Activity className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                  <p className="text-gray-600">Movement analysis will be displayed here</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Safety Tab */}
        {activeTab === 'safety' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-xl p-8 shadow-lg"
          >
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Safety Monitoring</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-green-50 rounded-lg">
                <Shield className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-gray-800">Impact Protection</h3>
                <p className="text-green-600 font-bold text-2xl">Active</p>
              </div>
              <div className="text-center p-6 bg-yellow-50 rounded-lg">
                <AlertTriangle className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-gray-800">Risk Level</h3>
                <p className="text-yellow-600 font-bold text-2xl">Low</p>
              </div>
              <div className="text-center p-6 bg-blue-50 rounded-lg">
                <Award className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-gray-800">Safety Score</h3>
                <p className="text-blue-600 font-bold text-2xl">94/100</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-xl p-8 shadow-lg"
          >
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Settings</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4 text-gray-800">Protection Settings</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">AI Protection Level</span>
                    <select className="px-3 py-2 border border-gray-300 rounded-lg">
                      <option>Maximum</option>
                      <option>High</option>
                      <option>Medium</option>
                      <option>Low</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Impact Sensitivity</span>
                    <input type="range" min="1" max="10" defaultValue="7" className="w-32" />
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4 text-gray-800">Notifications</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Performance Alerts</span>
                    <input type="checkbox" defaultChecked className="w-5 h-5 text-cresento-green-600" />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Safety Warnings</span>
                    <input type="checkbox" defaultChecked className="w-5 h-5 text-cresento-green-600" />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Training Reminders</span>
                    <input type="checkbox" className="w-5 h-5 text-cresento-green-600" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;